import React, {useEffect, useMemo, useState} from 'react';

import InputLabel from './InputLabel';
import InputTextField from './InputTextField';
import { toSafeNumber } from 'utils/helpers';
import IProduct from 'models/product';
import AiFieldVerdict from 'components/product/ProductUpdateForm/AiFieldVerdict';
import useViewport from 'hooks/useViewports';
import { getFormattedValue, getTemperatureError } from 'generated/productRequirements/validators';
export { getFormattedValue, getTemperatureError } from 'generated/productRequirements/validators';

interface IInputLabelProps {
  heading?: string,
  text?: string,
  required?: boolean,
};

interface IInputProps {
  inputName: keyof IProduct,
  onChange: (value: number, name: string) => void,
  setIsChanged?: (value: boolean) => void;
  value?: number,
  defaultValue?: number,
  placeholder?: string,
};

interface ITemperatureFieldProps {
  readOnly?: boolean;
  inputLabelProps: IInputLabelProps,
  minValueProps: IInputProps,
  maxValueProps: IInputProps,
  setTempError?: (value: boolean) => void;
};

export const TemperatureField = ({
  readOnly,
  inputLabelProps,
  minValueProps,
  maxValueProps,
  setTempError,
}: ITemperatureFieldProps) => {
  const { smUp } = useViewport();

  const [minValue, setMinValue] = useState<string | undefined>(getFormattedValue(""));
  const [maxValue, setMaxValue] = useState<string | undefined>(getFormattedValue(""));

  const [error, setError] = useState<string | undefined>(undefined);
  const [minHighlight, setMinHighlight] = useState(false);
  const [maxHighlight, setMaxHighlight] = useState(false);

  const isError = useMemo(() => !!error, [error]);

  const handleInputChange = (
    { target: { value, name } }: React.ChangeEvent<HTMLInputElement>,
    inputProps: IInputProps,
    setValue:(value: string) => void,
  ) => {
    const trimmedValue = value.trim();
    const currentValue = trimmedValue ? trimmedValue.replace(/[^0-9.,+-]/g, "") : "";
    const formattedValue = toSafeNumber(currentValue, null);

    inputProps.setIsChanged && inputProps.setIsChanged(true);
    inputProps.onChange(formattedValue, name);
    setValue(getFormattedValue(currentValue));
  };

  useEffect(() => {
    setMinValue(getFormattedValue(minValueProps.value));
  }, [minValueProps?.value]);

  useEffect(() => {
    setMaxValue(getFormattedValue(maxValueProps?.value));
  }, [maxValueProps?.value]);

  useEffect(() => {
    const error = getTemperatureError(minValueProps.value, maxValueProps.value);
    setError(error?.message);
    setMinHighlight(!!error?.min);
    setMaxHighlight(!!error?.max);
  }, [minValueProps.value, maxValueProps.value]);

  useEffect(() => {
    setTempError?.(isError);
  }, [isError, setTempError]);

  return (
    <>
      <InputLabel
        heading={inputLabelProps?.heading}
        text={inputLabelProps?.text}
        required={inputLabelProps?.required}
      />
      <div style={{ display: 'flex', flexDirection: 'row', flex: 1, gap: 10 }}>
        <div style={{ display: 'flex', flexDirection: smUp ? 'row' : 'column', flex: smUp ? undefined : 1 }}>
          <h3 style={{display: 'flex', alignItems: 'center', margin: '0 10px 0 0'}}>
            Min:
          </h3>
          <InputTextField<IProduct>
            readOnly={readOnly}
            forceRed={minHighlight}
            placeholder={minValueProps?.placeholder}
            inputName={minValueProps.inputName}
            hideAiVerdict
            value={minValue}
            style={{ maxWidth: smUp ? 150 : undefined }}
            onChange={(e) => handleInputChange(e, minValueProps, setMinValue)}
          />
        </div>
        <div style={{ display: 'flex', flexDirection: smUp ? 'row' : 'column', flex: smUp ? undefined : 1 }}>
          <h3 style={{display: 'flex', alignItems: 'center', margin: '0 10px 0 0'}}>
            Max:
          </h3>
          <InputTextField<IProduct>
            readOnly={readOnly}
            forceRed={maxHighlight}
            placeholder={maxValueProps?.placeholder}
            inputName={maxValueProps.inputName}
            hideAiVerdict
            value={maxValue}
            style={{ maxWidth: smUp ? 150 : undefined }}
            onChange={(e) => handleInputChange(e, maxValueProps, setMaxValue)}
          />
        </div>
      </div>
      {error && (
        <span style={{ fontWeight: 'bold', color: 'orangered' }}>
          {error}
        </span>
      )}
      {/* Min and max share a row, so their AI review verdicts come after it and its error. */}
      <AiFieldVerdict field={String(minValueProps.inputName)} />
      <AiFieldVerdict field={String(maxValueProps.inputName)} />
    </>
  );
};

export default TemperatureField;
